import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { MessageCircle, Send, Search, Check, CheckCheck, Users, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 3000; // 3 seconds for real-time feel

const ChatApp = () => {
  const { getAuthHeader, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/erp/communication/chat/users`, {
        headers: getAuthHeader()
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [getAuthHeader]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/erp/communication/chat/conversations`, {
        headers: getAuthHeader()
      });
      setConversations(res.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [getAuthHeader]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/erp/communication/chat/messages/${conversationId}`,
        { headers: getAuthHeader() }
      );
      setMessages(res.data);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchUsers();
    fetchConversations();
  }, [fetchUsers, fetchConversations]);

  // Real-time polling for new messages
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.conversation_id);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchConversations, fetchMessages, selectedConversation]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const payload = {
        message: newMessage,
        recipient_emails: selectedConversation 
          ? selectedConversation.participants 
          : selectedUsers,
        conversation_id: selectedConversation?.conversation_id
      };

      await axios.post(`${BACKEND_URL}/api/erp/communication/chat/send`, payload, {
        headers: getAuthHeader()
      });

      setNewMessage('');
      
      if (selectedConversation) {
        fetchMessages(selectedConversation.conversation_id);
      } else {
        // New conversation created
        setShowNewChat(false);
        setSelectedUsers([]);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setShowNewChat(false);
    fetchMessages(conv.conversation_id);
  };

  const handleNewChat = () => {
    setShowNewChat(true);
    setSelectedConversation(null);
    setMessages([]);
    setSelectedUsers([]);
  };

  const toggleUserSelection = (email) => {
    setSelectedUsers(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    
    if (hours < 1) {
      const mins = Math.floor(diff / 60000);
      return mins < 1 ? 'Just now' : `${mins}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Conversations List */}
      <div className="w-1/3 border-r flex flex-col">
        <div className="p-4 border-b bg-primary text-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <MessageCircle size={20} />
              Messages
            </h2>
            <Button 
              size="sm" 
              onClick={handleNewChat}
              className="bg-white text-primary hover:bg-gray-100"
            >
              New Chat
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle size={48} className="mx-auto mb-2 text-gray-300" />
              <p>No conversations yet</p>
              <p className="text-sm">Start a new chat!</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.conversation_id}
                onClick={() => handleSelectConversation(conv)}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedConversation?.conversation_id === conv.conversation_id 
                    ? 'bg-blue-50 border-l-4 border-primary' 
                    : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                        {conv.participants.length > 1 ? (
                          <Users size={20} />
                        ) : (
                          conv.participants[0]?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">
                          {conv.participants.join(', ')}
                        </p>
                        <p className="text-xs text-gray-600 truncate">
                          {conv.sender_name}: {conv.last_message}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-2">
                    <p className="text-xs text-gray-500">
                      {formatTime(conv.last_message_time)}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {showNewChat ? (
          <>
            {/* New Chat Header */}
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="font-bold">New Chat</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowNewChat(false)}>
                <X size={20} />
              </Button>
            </div>

            {/* User Selection */}
            <div className="p-4 border-b">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {selectedUsers.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedUsers.map(email => {
                    const u = users.find(user => user.email === email);
                    return (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-full text-sm"
                      >
                        {u?.full_name || email}
                        <X 
                          size={14} 
                          className="cursor-pointer" 
                          onClick={() => toggleUserSelection(email)}
                        />
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filteredUsers.map(u => (
                <div
                  key={u.email}
                  onClick={() => toggleUserSelection(u.email)}
                  className={`p-3 mb-2 rounded-lg cursor-pointer transition-colors ${
                    selectedUsers.includes(u.email)
                      ? 'bg-primary text-white'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <p className="font-semibold">{u.full_name}</p>
                  <p className="text-sm opacity-80">{u.email}</p>
                  <p className="text-xs opacity-60">{u.role?.toUpperCase()}</p>
                </div>
              ))}
            </div>

            {/* Send Area for New Chat */}
            {selectedUsers.length > 0 && (
              <div className="p-4 border-t bg-gray-50">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button onClick={handleSendMessage} className="bg-primary">
                    <Send size={20} />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                  {selectedConversation.participants.length > 1 ? (
                    <Users size={20} />
                  ) : (
                    selectedConversation.participants[0]?.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <p className="font-semibold">
                    {selectedConversation.participants.join(', ')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedConversation.participants.length} participant(s)
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {messages.map((msg, idx) => {
                const isOwn = msg.sender_email === user?.email;
                const isRead = msg.read_by?.length > 0;

                return (
                  <div
                    key={msg.id}
                    className={`mb-3 flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      isOwn 
                        ? 'bg-primary text-white' 
                        : 'bg-white border'
                    }`}>
                      {!isOwn && (
                        <p className="text-xs font-semibold mb-1 opacity-70">
                          {msg.sender_name}
                        </p>
                      )}
                      <p className="text-sm">{msg.message}</p>
                      <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                        isOwn ? 'text-white/70' : 'text-gray-500'
                      }`}>
                        <span>{formatTime(msg.created_at)}</span>
                        {isOwn && (
                          isRead ? (
                            <CheckCheck size={14} className="text-blue-300" />
                          ) : (
                            <Check size={14} />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button onClick={handleSendMessage} className="bg-primary">
                  <Send size={20} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageCircle size={64} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg font-semibold">Select a chat to start messaging</p>
              <p className="text-sm">or create a new conversation</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatApp;
